'use client'

import { useLocale, useTranslations } from 'next-intl'
import styles from './Menu.module.css'
import MenuItem from './MenuItem/MenuItem'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react'
import LinkButton, { LinkButtonKind, LinkButtonSize } from '@/components/UI/Buttons/LinkButton/LinkButton'
import IconButton, { IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import IconSearch from '@/assets/img/Icon_Search.svg'
import IconUser from '@/assets/img/Icon_User.svg'
import LogotypeColor from '@/assets/img/LogotypeColor.svg'
import { InputAdornment, TextField } from '@mui/material'
import Button, { ButtonKind, ButtonSize } from '@/components/UI/Buttons/Button/Button'
import { useQueueStore } from '@/stores/useQueueStore'
import { useModalStore } from '@/stores/useModalStore'
import QueueModal from '@/components/Modals/QueueModal/QueueModal'
// import LanguageSelector from './LanguageSelector/LanguageSelector'
import { useUserStore } from '@/stores/useUserStore'
import getMenuData from './MenuData'
import { useWindowSize } from '@/hooks/useWindowsSize'
import { BREAKPOINTS, FEATURES } from '@/utils/constants'
import LinkIconButton from '@/components/UI/Buttons/LinkIconButton/LinkIconButton'
import Avatar, { AvatarSizes } from '@/components/UI/Avatar/Avatar'
import Submenu from './Submenu/Submenu'
import { pushToDataLayer, removeLocaleFromPath, shouldHideMenu } from '@/utils/helpers'
import { ProfileType } from '@/interfaces/profile_types'
import { useProfiles } from '@/apiUtilities/queries/useProfiles'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getCatalogue } from '@/apiUtilities/requests/games'
import Label, { LabelKind } from '../UI/Label/Label'
import { GameType } from '@/interfaces/games_types'
import { ImageKey } from '@/interfaces/genericTypes.types'
import { Link } from '@/i18n/navigation'

export default function Menu() {
	const { userId, profileId } = useUserStore()
	const t = useTranslations('Menu')
	const { getQueue, queues } = useQueueStore()
	const { openModal, closeModal } = useModalStore()
	// Normalize pathname once
  const rawPath = usePathname()
  const pathname = useMemo (
		() => removeLocaleFromPath(rawPath),
		[rawPath]
	)
	const searchParams = useSearchParams()

  const menuData = getMenuData().filter(el => (el.guard ? el.guard() : true))
	const selectedKey = pathname.replace('/', '')
	console.log('Pathname: ', pathname)
	const { data: profiles } = useProfiles({ enabled: !!userId })

	const mainRef = useRef<HTMLDivElement | null>(null)
	const selectedItemRef = useRef<HTMLDivElement | null>(null)
	const { width } = useWindowSize()
	const [centerX, setCenterX] = useState<number | undefined>(undefined)
	const [showSearch, setShowSearch] = useState<boolean>(false)
	const [showSubmenu, setShowSubmenu] = useState<boolean>(false)
	const isMobile = width < BREAKPOINTS.DESKTOP || width === 0
	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')
	const locale = useLocale()

	const loginLink =
    !FEATURES.loginExternal
      ? '/login'
      : (process.env.NEXT_PUBLIC_FEATURE_LOGIN_EXTERNAL_LINK ?? '')

	const avatarURL = useMemo(() => {
		return profiles?.find((elem: ProfileType) => elem.id === profileId)?.avatar.image_path ?? ''
	}, [profiles, profileId])

	const avatarSrc = avatarURL
  	? `${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}${avatarURL}`
  	: undefined

	/**
	 * Update selected menu indicator position based on pathname
	 */
	useLayoutEffect(() => {
		if (mainRef.current && selectedItemRef.current) {
			const parentRect = mainRef.current.getBoundingClientRect()
			const childRect = selectedItemRef.current.getBoundingClientRect()
			const xCenter = childRect.left - parentRect.left + childRect.width / 2
			setCenterX(xCenter)
		} else {
			// Important: reset the variable if the pathname does not match any menu item
			setCenterX(undefined)
		}
	}, [pathname, width])

	/**
	 * Hide menu on specific routes or when width is 0 (to avoid hydration issues)
	 */
	const hiddenMenu = useMemo(() => {
		return shouldHideMenu(pathname, searchParams.toString()) || width === 0
	}, [pathname, searchParams, width])

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(search)
			pushToDataLayer({
				event: 'search_submit',
				search_location: 'menu',
				search_term: search
			})
		}, 500)
		return () => clearTimeout(handler)
	}, [search])

	const searchResults = useQuery({
		queryKey: ['games', debouncedSearch],
		queryFn: () => getCatalogue(`page=0&size=5&lang=${locale}${profileId ? `&profile_id=${profileId}` : ''}&search=${debouncedSearch}`),
		enabled: debouncedSearch.trim() !== ''
	})
	
	const router = useRouter()
	const queryClient = useQueryClient()
	function clearSearchResults() {
		setSearch('')
		queryClient.removeQueries({ queryKey: ['games'] })
	}

	function handleAdvancedResults() {
		const currentSearch = search
		clearSearchResults()
		setShowSearch(false)
		router.push(`/catalogue?search=${currentSearch}`)
	}

	function handleCancelSearch() {
		clearSearchResults()
		setShowSearch(false)
	}

	function handleResultClick(gameId: string) {
		clearSearchResults()
		setShowSearch(false)
		router.push(`/games/${gameId}`)
	}

	const [prevSearch, setPrevSearch] = useState('')

	useEffect(() => {
		if(search !== '' && search !== prevSearch) {
			pushToDataLayer({
				event: 'search_submit',
				search_term: search,
				search_location: 'menu',
				results_count: searchResults.data && searchResults.data.pages ? searchResults.data.pages[0].total : 0
			})
			setPrevSearch(search)
		}
	}, [searchResults.data])
	
	const [mounted, setMounted] = useState(false)

	useEffect(() => {
	setMounted(true)
	}, [])

	if (!mounted) return null
	return (
		<nav className={`${styles.nav} ${!isMobile ? styles.topMenu : ''}`}>
			<div className={`${styles.container} ${hiddenMenu ? styles.hidden : ''}`}>
				<div className={styles.logo}>
					<Link href='/'>
						<LogotypeColor />
					</Link>
				</div>

				{!hiddenMenu && !showSearch && selectedKey !== 'select-profile' &&
				<div className={styles.menu} ref={mainRef}>
					{menuData.map(item => {
						console.log(item)
						console.log(selectedKey)
						const isSelected = item.url.replace('/', '') === selectedKey
						return (
							<MenuItem item={item} key={item.title} ref={isSelected ? selectedItemRef : undefined}/>
						)
					})}

					{userId && profileId && isMobile && avatarSrc &&
						<Avatar
							backgroundUrl={avatarSrc}
							size={AvatarSizes.XS}
							onClick={() => {
								pushToDataLayer({
									event: "home_menu_click",
									page_name: pathname,
									user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
									menu_item: "avatar"
								})
								setShowSubmenu(true)
							}}
						/>
						}

					{centerX &&
					<div
						className={styles.selectedBar}
						style={{ left: centerX }}
					/>
					}
				</div>
				}

				{!hiddenMenu &&
				<div className={styles.sideControls}>
					{/* Cola: mostrar posición si existe para este perfil */}
					{userId && profileId && getQueue(profileId) && !showSearch && (
						/* <div className={styles.queueIndicator} style={{marginRight: 12, cursor: 'pointer'}} onClick={() => openModal({ content: <QueueModal onClose={closeModal}/> })}>
							<span style={{background: '#f90', color: '#fff', borderRadius: 12, padding: '2px 10px', fontWeight: 700, fontSize: 14}}>
								{`#${getQueue(profileId).position}`}
							</span>
						</div> */
						<Button pulseAnimation={getQueue(profileId) !== undefined ? getQueue(profileId)!.position <= 3 : false } kind={ButtonKind.SUGGESTED} className={styles.queueButton} onClick={
							() => {
								pushToDataLayer({
									event: "home_menu_click",
									page_name: pathname,
									user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
									menu_item: "queue_indicator"
								})
								openModal({ content: <QueueModal onClose={closeModal}/> })
							}}>{getQueue(profileId)?.position}</Button>
					)}
					{showSearch &&
					<div className={styles.searchControls}>
						<div className={styles.resultsWrapper}>
							<TextField label={'Search'}
								onChange={e => setSearch(e.target.value)}
								value={search}
								slotProps={{
									input: {
										endAdornment:
										<InputAdornment position="end">
											<IconSearch width={'1.25rem'} height={'1.25rem'}/>
										</InputAdornment>
									}
								}}
							/>
							{searchResults.isSuccess && searchResults.data.games.length > 0 &&
							<div className={styles.results}>
								{searchResults.data.games.map((game: GameType) => {
									const sizes = [400, 300, 200, 150, 133, 100, 75];
									let srcHorizontal = null
									if(game.thumb_horizontal) {
										for (const size of sizes) {
											const key = `esc_${String(size).padStart(3, '0')}_url` as ImageKey
												if (game.thumb_horizontal[key]) {
													srcHorizontal = game.thumb_horizontal[key]
													break;
												}
										}
									}

									let labelKind = undefined
									if(game.recommended) labelKind = LabelKind.RECOMMENDED
									if(game.new_entry) labelKind = LabelKind.NEW
									if(game.type === 'steam') labelKind = LabelKind.STEAM_LICENSE
									if(game.type === 'epic') labelKind = LabelKind.EPIC_LICENSE
									if(game.type === 'battlenet') labelKind = LabelKind.BATTLENET_LICENSE

									return (
										<div key={game.game_id} className={styles.result} onClick={() => handleResultClick(game.game_id)} style={{ cursor: 'pointer' }}>
											<div className={styles.cardImageWrapper}>
												<img src={srcHorizontal ? process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + srcHorizontal : undefined} alt={game.title} key={game.game_id} className={`${styles.cardImage} ${!(userId && profileId) && game.blur_for_guests ? styles.blur : ''}`}/>
											</div>
											<div className={styles.resultInfo}>
												<p className='mediumButton'>{game.title}</p>
												{labelKind && <Label kind={labelKind} info={false} />}
											</div>
										</div>
									)
								})}
								<Button onClick={handleAdvancedResults}>{t('advancedResults')}</Button>
							</div>
							}
						</div>
						<Button size={ButtonSize.MEDIUM} onClick={handleCancelSearch}>{t('cancel')}</Button>
					</div>
					}

					{!showSearch &&
					<div className={styles.menuControls}>
						{!isMobile && !pathname.startsWith('/catalogue') && <IconButton title={'Search'} icon={IconSearch} size={IconButtonSize.L} onClick={() => {
							pushToDataLayer({
								event: "home_menu_click",
								page_name: pathname,
								user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
								menu_item: "search"
							})
						setShowSearch(true)
						}}/>}

						{FEATURES.login && !userId && !isMobile && 
						<LinkButton
							href={loginLink}
							kind={LinkButtonKind.SUGGESTED}
							size={LinkButtonSize.MEDIUM}
							target={FEATURES.loginExternal ? '_blank' : undefined}
							onClick={() => {
								pushToDataLayer({
									event: "home_menu_click",
									page_name: pathname,
									user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
									menu_item: "login"
								})
							}}
						>
							<IconUser />
							{t('login')}
						</LinkButton>
						}

						{FEATURES.login && !userId && isMobile && 
							<LinkIconButton
								href={loginLink} 
								icon={IconUser}
								size={IconButtonSize.M}
								selected={true}
								target={FEATURES.loginExternal ? '_blank' : undefined}
								onClick={() => {
									pushToDataLayer({
										event: "home_menu_click",
										page_name: pathname,
										user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
										menu_item: "login"
									})
								}}
							/>
						}

						{userId && profileId && !isMobile && avatarSrc && 
						<Avatar
							backgroundUrl={avatarSrc}
							size={AvatarSizes.XS}
							onClick={() => {
								pushToDataLayer({
									event: "home_menu_click",
									page_name: pathname,
									user_state: userId && profileId ? 'loggedIn' : 'loggedOut',
									menu_item: "avatar"
								})
								setShowSubmenu(true)
							}}
						/>
						}
					</div>
					}
				</div>
				}
			</div>

			{showSubmenu && 
			<Submenu
				onClose={() => setShowSubmenu(false)}
			/>
			}
		</nav>
	)
}